import React, { useEffect, useState } from 'react';
import {
  getAllTeachers,
  addTeacher,
  updateTeacher,
  deleteTeacher,
} from '../../services/api';

const AdminTeachers = () => {
  const [teachers, setTeachers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    full_name: '',
    degree: '',
    major: '',
    phone_number: '',
    workplace: '',
    note: '',
    experience: '',
    awards: '',
    image_url: '',
    teaching_method: '',
  });

  const itemsPerPage = 6;

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      const res = await getAllTeachers();
      setTeachers(res.data);
    } catch (err) {
      console.error('Lỗi khi lấy danh sách giáo viên:', err);
    }
  };

  const handleAddTeacher = () => {
    setFormData({
      full_name: '',
      degree: '',
      major: '',
      phone_number: '',
      workplace: '',
      note: '',
      experience: '',
      awards: '',
      image_url: '',
      teaching_method: '',
    });
    setEditingTeacher(null);
    setShowAddModal(true);
  };

  const handleEditTeacher = (teacher) => {
    setFormData(teacher);
    setEditingTeacher(teacher);
    setShowAddModal(true);
  };

  const handleDeleteTeacher = async (id) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa giáo viên này?')) {
      await deleteTeacher(id);
      fetchTeachers();
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTeacher) {
        await updateTeacher(editingTeacher.id, formData);
      } else {
        await addTeacher(formData);
      }
      fetchTeachers();
      setShowAddModal(false);
    } catch (err) {
      console.error('Lỗi khi lưu giáo viên:', err);
    }
  };

  const getStatusColor = (status) => {
    return status === 'Hoạt động'
      ? 'text-green-600 bg-green-100'
      : 'text-red-600 bg-red-100';
  };

  const filteredTeachers = teachers.filter((teacher) =>
    teacher?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher?.major?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher?.workplace?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const totalPages = Math.ceil(filteredTeachers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTeachers = filteredTeachers.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Quản Lý Giáo Viên</h1>
        <p className="text-gray-600">Quản lý thông tin và lịch dạy của giáo viên</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Tìm kiếm giáo viên..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={handleAddTeacher}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Thêm Giáo Viên
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {currentTeachers.map((teacher) => (
          <div
            key={teacher.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-semibold mr-4">
                {teacher.full_name?.split(' ').slice(-2).map(w => w[0]).join('')}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{teacher.full_name}</h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-green-600 bg-green-100 px-2 py-1 rounded">
                    {teacher.major}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${getStatusColor('Hoạt động')}`}>
                    Hoạt động
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2 text-sm text-gray-600 mb-4">
              <div className="flex items-center">
                <span className="w-4 h-4 mr-2">⭐</span>
                <span>{teacher.experience}</span>
              </div>
              <div className="flex items-center">
                <span className="w-4 h-4 mr-2">🏫</span>
                <span className="truncate">{teacher.workplace}</span>
              </div>
              <div className="flex items-center">
                <span className="w-4 h-4 mr-2">📱</span>
                <span>{teacher.phone_number}</span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleEditTeacher(teacher)}
                className="flex-1 bg-blue-50 text-blue-600 py-2 px-4 rounded-lg hover:bg-blue-100 transition-colors text-sm"
              >
                Chỉnh sửa
              </button>
              <button
                onClick={() => handleDeleteTeacher(teacher.id)}
                className="flex-1 bg-red-50 text-red-600 py-2 px-4 rounded-lg hover:bg-red-100 transition-colors text-sm"
              >
                Xóa
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-center mt-6 space-x-2">
        {Array.from({ length: totalPages }, (_, index) => (
          <button
            key={index}
            onClick={() => setCurrentPage(index + 1)}
            className={`px-3 py-1 rounded ${currentPage === index + 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            {index + 1}
          </button>
        ))}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-xl shadow-lg">
            <h2 className="text-xl font-semibold mb-4">{editingTeacher ? 'Chỉnh sửa' : 'Thêm'} Giáo Viên</h2>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              {Object.entries({
                full_name: 'Họ và tên',
                degree: 'Trình độ',
                major: 'Chuyên ngành',
                phone_number: 'Số điện thoại',
                workplace: 'Nơi công tác',
                note: 'Ghi chú',
                experience: 'Kinh nghiệm',
                awards: 'Giải thưởng',
                image_url: 'Ảnh đại diện (URL)',
                teaching_method: 'Phương pháp giảng dạy',
              }).map(([key, label]) => (
                <input
                  key={key}
                  type="text"
                  placeholder={label}
                  value={formData[key]}
                  onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              ))}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 bg-gray-300 rounded-lg"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                >
                  Lưu
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTeachers;
